import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Users, Phone, Mail, Briefcase, Building, Search, 
  RefreshCw, Loader2, UserCheck, Calendar, MapPin, AlertTriangle,
  Clock, CheckCircle, XCircle, CalendarDays
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const MyTeam = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingLeaveRequests, setPendingLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  const { getAuthHeaders, currentOffice, user } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, [currentOffice]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }

      // Fetch team members and pending leave requests in parallel
      const [teamRes, leaveRes] = await Promise.all([
        fetch(`${API_URL}/api/employees/my-team`, { headers }),
        fetch(`${API_URL}/api/leave/team-requests`, { headers })
      ]);

      if (teamRes.ok) {
        const myTeam = await teamRes.json();
        setTeamMembers(myTeam);
      } else {
        setError('Unable to fetch team members');
      }

      if (leaveRes.ok) {
        const leaveRequests = await leaveRes.json();
        setPendingLeaveRequests(leaveRequests);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveLeave = async (requestId) => {
    setProcessingId(requestId);
    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';

      const response = await fetch(`${API_URL}/api/leave/request/${requestId}/manager-approve`, {
        method: 'PUT',
        headers
      });

      if (response.ok) {
        // Remove from pending list
        setPendingLeaveRequests(prev => prev.filter(r => r.id !== requestId));
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to approve leave request');
      }
    } catch (err) {
      console.error('Error approving leave:', err);
      alert('Network error');
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectModal = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleRejectLeave = async () => {
    if (!selectedRequest) return;
    
    setProcessingId(selectedRequest.id);
    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';

      const url = new URL(`${API_URL}/api/leave/request/${selectedRequest.id}/manager-reject`);
      if (rejectionReason) {
        url.searchParams.append('rejection_reason', rejectionReason);
      }

      const response = await fetch(url.toString(), {
        method: 'PUT',
        headers
      });

      if (response.ok) {
        setPendingLeaveRequests(prev => prev.filter(r => r.id !== selectedRequest.id));
        setShowRejectModal(false);
        setSelectedRequest(null);
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to reject leave request');
      }
    } catch (err) {
      console.error('Error rejecting leave:', err);
      alert('Network error');
    } finally {
      setProcessingId(null);
    }
  };

  const filteredMembers = teamMembers.filter(member => {
    const searchLower = searchQuery.toLowerCase();
    return !searchQuery || 
      member.first_name?.toLowerCase().includes(searchLower) ||
      member.last_name?.toLowerCase().includes(searchLower) ||
      member.employment?.job_title?.toLowerCase().includes(searchLower) ||
      member.employment?.department?.toLowerCase().includes(searchLower);
  });

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge className="bg-green-100 text-green-700">Active</Badge>
    ) : (
      <Badge variant="secondary">Inactive</Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users className="h-6 w-6" />
            My Team
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View and manage team members reporting to you
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Team Members</p>
                <p className="text-3xl font-bold text-blue-600">{teamMembers.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Leave Requests</p>
                <p className="text-3xl font-bold text-orange-600">{pendingLeaveRequests.length}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="h-8 w-8 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Leave Requests Section */}
      {pendingLeaveRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-orange-600" />
              Leave Requests Pending Your Approval
            </CardTitle>
            <CardDescription>
              Review and approve or reject leave requests from your team members
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pendingLeaveRequests.map((request) => (
                <div 
                  key={request.id} 
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg bg-orange-50 dark:bg-orange-900/10"
                  data-testid={`leave-request-${request.id}`}
                >
                  <div className="space-y-1 mb-4 sm:mb-0">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {request.employee_name}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <Badge variant="outline" className="mr-2">{request.leave_type}</Badge>
                      {request.days_requested} day{request.days_requested !== 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDate(request.start_date)} - {formatDate(request.end_date)}
                    </p>
                    {request.reason && (
                      <p className="text-sm text-gray-500 italic">"{request.reason}"</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => handleApproveLeave(request.id)}
                      disabled={processingId === request.id}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`approve-leave-${request.id}`}
                    >
                      {processingId === request.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </>
                      )}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => openRejectModal(request)}
                      disabled={processingId === request.id}
                      data-testid={`reject-leave-${request.id}`}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      {teamMembers.length > 0 && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search team members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="team-search"
          />
        </div>
      )}

      {/* Team Members Grid */}
      {filteredMembers.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {teamMembers.length === 0 ? 'No Team Members' : 'No Results Found'}
            </h3>
            <p className="text-gray-500">
              {teamMembers.length === 0 
                ? 'You don\'t have any team members reporting to you yet.'
                : 'Try adjusting your search query.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMembers.map((member) => (
            <Card key={member.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      member.is_active ? 'bg-blue-100' : 'bg-gray-200'
                    }`}>
                      <span className={`font-bold text-lg ${
                        member.is_active ? 'text-blue-600' : 'text-gray-400'
                      }`}>
                        {member.first_name?.[0]}{member.last_name?.[0]}
                      </span>
                    </div>
                    <div>
                      <CardTitle className="text-lg">
                        {member.first_name} {member.last_name}
                      </CardTitle>
                      <p className="text-sm text-gray-500">
                        {member.employment?.employee_id || 'No ID'}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(member.is_active)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Job Info */}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Briefcase className="h-4 w-4" />
                    <span>{member.employment?.job_title || 'No title'}</span>
                  </div>
                  {member.employment?.department && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Building className="h-4 w-4" />
                      <span>{member.employment.department}</span>
                    </div>
                  )}

                  {/* Contact Info */}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="h-4 w-4" />
                    <span>{member.contact?.phone || 'No phone'}</span>
                  </div>
                  {member.contact?.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="h-4 w-4" />
                      <span className="truncate">{member.contact.email}</span>
                    </div>
                  )}

                  {/* Work Location */}
                  {member.employment?.work_location && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{member.employment.work_location}</span>
                    </div>
                  )}

                  {/* Hire Date */}
                  {member.employment?.hire_date && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>Hired: {new Date(member.employment.hire_date).toLocaleDateString()}</span>
                    </div>
                  )}

                  {/* Employment Type Badge */}
                  <div className="pt-2 border-t flex gap-2">
                    <Badge variant="outline">{member.employment?.employment_type || 'Full-Time'}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Leave Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to reject the leave request from <strong>{selectedRequest?.employee_name}</strong>?
            </p>
            <div>
              <label className="text-sm font-medium">Reason for Rejection (Optional)</label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Provide a reason for rejecting this request..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectLeave} disabled={processingId}>
              {processingId ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyTeam;
